import { ChangeDetectorRef, Component, EventEmitter, inject, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DeviceDetectorService } from 'ngx-device-detector';
import { MaterialModule } from '../../../shared/material/material.module';
import {

  DocumentSharing,
  DocumentSharingControllerService,
  ProfileProviderPfControllerService,
  ProfileProviderPJ,
  ProfileProviderPjControllerService,
  UserRef,
  UserRefControllerService,
} from '../../../core/generated';
import { ActivatedRoute } from '@angular/router';
import { maskConfig } from '../../../shared/maskConfiguration';
import { NgxMaskDirective } from 'ngx-mask';
import { FormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { isValidCNPJ, isValidCPF } from '../../../shared/utils/validators';
import { DocumentSharingComponent } from '../../../shared/document-sharing/document-sharing.component';
import { PlanStatusService } from '../../../shared/services/plan-status-cache.service';
import { Feature } from '../../../shared/config/package-features.config';
import { UserRefCacheListService } from '../../../shared/services/user-ref-cache-list.service';
import { ProfileProviderPJCacheListService } from '../../../shared/services/profile-provider-pj-cache-list.service';
import { ContainerComponent } from '../../../shared/container/container.component';

@Component({
    selector: 'app-share-documents-profile-pf',
    standalone: true,
    imports: [
        CommonModule,
        MaterialModule,
        NgxMaskDirective,
        ReactiveFormsModule, 
        DocumentSharingComponent,
        ContainerComponent
    ],
    templateUrl: './share-documents-profile-pj.component.html',
    styleUrl: './share-documents-profile-pj.component.scss'
})
export class ShareDocumentsProfilePJComponent implements OnInit {
  route = inject(ActivatedRoute);
  deviceService = inject(DeviceDetectorService);
  profileProviderPFService = inject(ProfileProviderPfControllerService);
  isMobile = this.deviceService.isMobile();
  documentSharingService = inject(DocumentSharingControllerService);
  profileProviderPJService = inject(ProfileProviderPjControllerService);
  userRefService = inject(UserRefControllerService);
  cd = inject(ChangeDetectorRef);
  planStatusService = inject(PlanStatusService);
  userRefCacheService = inject(UserRefCacheListService);
  profileProviderPJCacheService = inject(ProfileProviderPJCacheListService);

  profileProviderPJFound: ProfileProviderPJ | undefined;
  userRefFound: UserRef | undefined;
  documentSharingFound: DocumentSharing | undefined;
  addToShared: DocumentSharing | undefined; // Para "compartilhados com"
  addToAccess: DocumentSharing | undefined; // Para "meus acessos"
  profileProviderPJ!: ProfileProviderPJ;
  isLoading = false;

  @ViewChild('sharedComponent', { read: DocumentSharingComponent })
  sharedDocumentComponent?: DocumentSharingComponent;

  @ViewChild('accessComponent', { read: DocumentSharingComponent })
  accessDocumentComponent?: DocumentSharingComponent;

  documentSharingList?: DocumentSharing[];
  pjId: string | null = null;
  maskConfig = maskConfig;
  cpfcnpjControl = new FormControl('', [Validators.required]);
  shareTypeControl = new FormControl('', [Validators.required]);

  // Expor Feature enum para uso no template
  readonly Feature = Feature;

  ngOnInit(): void {
    const idParam = this.route.parent?.snapshot.paramMap.get('id');
    this.pjId = idParam ?? null;
    this.isMobile = this.deviceService.isMobile();
    this.shareTypeControl.setValue('share');
    this.profileProviderPJService.findById6(Number(this.pjId!)).subscribe((profile) => {
      this.profileProviderPJ = profile;
    });
  }

  shareDocuments(): void {
    let documentSharing: DocumentSharing;
    let isShareAction = this.shareTypeControl.value === 'share';
    
    if (isShareAction) {
      documentSharing = {
        ownerDocumentsCpfCnpj: this.profileProviderPJ.cnpj,
        grantedCompanyCnpj: this.profileProviderPJFound!.cnpj
      };
    } else {
      documentSharing = {
        ownerDocumentsCpfCnpj: this.userRefFound ? this.userRefFound.cpf : this.profileProviderPJFound ? this.profileProviderPJFound.cnpj : '',
        grantedCompanyCnpj: this.profileProviderPJ.cnpj
      };
    }
    
    this.documentSharingService.create(documentSharing).subscribe((response: DocumentSharing) => {
      // Define qual input recebe a atualização baseado no tipo de ação
      if (isShareAction) {
        // Atualiza o componente "compartilhados com"
        this.addToShared = response;
        setTimeout(() => this.addToShared = undefined, 100);
      } else {
        // Atualiza o componente "meus acessos"
        this.addToAccess = response;
        setTimeout(() => this.addToAccess = undefined, 100);
      }
      
      this.cd.detectChanges();
    });

    this.cpfcnpjControl.reset();
    this.userRefFound = undefined;
    this.profileProviderPJFound = undefined;
  }

  searchCpfOrCnpj(): void {
    if (this.cpfcnpjControl.value) {
      const isShareAction = this.shareTypeControl.value === 'share';
      
      // Se for compartilhar (share), só aceita CNPJ
      if (isShareAction) {
        if (this.cpfcnpjControl.value.length === 14 && isValidCNPJ(this.cpfcnpjControl.value)) {
          this.profileProviderPJCacheService.findProfileProviderPJByCnpj(this.cpfcnpjControl.value, (pj) => {
            if (pj) {
              this.profileProviderPJFound = pj;
              this.cd.detectChanges();
            }
          });
        }
      } else {
        // Se for solicitar acesso (request), aceita CPF ou CNPJ
        if (this.cpfcnpjControl.value.length === 11 && isValidCPF(this.cpfcnpjControl.value)) {
          this.userRefCacheService.findUserRefByCpf(this.cpfcnpjControl.value, (user) => {
            if (user) {
              this.userRefFound = user;
              this.cd.detectChanges();
            }
          });
        }

        if (this.cpfcnpjControl.value.length === 14 && isValidCNPJ(this.cpfcnpjControl.value)) {
          this.profileProviderPJCacheService.findProfileProviderPJByCnpj(this.cpfcnpjControl.value, (pj) => {
            if (pj) {
              this.profileProviderPJFound = pj;
              this.cd.detectChanges();
            }
          });
        }
      }

      this.cpfcnpjControl.reset();
    }
  }

  isCpfOrCnpjInvalid(): boolean {
    if (!this.cpfcnpjControl.value) {
      return false;
    }
    
    const isShareAction = this.shareTypeControl.value === 'share';
    
    // Se for compartilhar (share), só valida CNPJ
    if (isShareAction) {
      return isValidCNPJ(this.cpfcnpjControl.value);
    }
    
    // Se for solicitar acesso (request), valida CPF ou CNPJ
    return (isValidCPF(this.cpfcnpjControl.value) || isValidCNPJ(this.cpfcnpjControl.value));
  }

  // Métodos de controle de acesso (seguindo padrão do sidebar)
  canAccess(cpfCnpj: string | undefined | null, feature?: Feature | Feature[]): boolean {
    if (!cpfCnpj) return false;
    return this.planStatusService.canAccessFeature(cpfCnpj, feature).hasAccess;
  }

  hasFeatureAccess(cpfCnpj: string | undefined | null, feature: Feature | Feature[]): boolean {
    if (!cpfCnpj) return false;
    return this.planStatusService.hasFeatureAccess(cpfCnpj, feature);
  }

}
